import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import api from '../utils/api';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import jsPDF from 'jspdf';
import '../css/AdminDashboard.css';
import logo from "../assets/lna.png";

const ENDPOINTS = {
  APPLICATIONS: '/members/applications',
  MEMBERS: '/members',
  SUBSCRIBERS: '/members/subscribers',
  APPROVE_APPLICATION: (empNo) => `/members/approve/${empNo}`,
  UPDATE_APPLICATION: (empNo) => `/members/applications/${empNo}`,
  UPDATE_MEMBER: (empNo) => `/members/${empNo}`,
  DELETE_APPLICATION: (empNo) => `/members/applications/${empNo}`,
  DELETE_MEMBER: (empNo) => `/members/${empNo}`,
};

const TABLE_HEADERS = [
  'Title', 'Name', 'Email', 'Gender', 'Marital Status', 'ID Number',
  'Employment Number', 'Grade', 'Phone', 'Nationality', 'DOB',
  'Employer', 'Duty Station', 'District', 'Type of Employment',
  'Payment Frequency', 'Payment Method', 'Status', 'Actions'
];

const MENU_ITEMS = [
  { key: 'dashboard', icon: '📊', label: 'Dashboard' },
  { key: 'applications', icon: '📝', label: 'Applications' },
  { key: 'members', icon: '👥', label: 'Members' },
  { key: 'manualPayers', icon: '💳', label: 'Manual Payers' },
  { key: 'transactions', icon: '💰', label: 'Transactions' },
  { key: 'subscribers', icon: '📧', label: 'Subscribers' }
];

const PAYMENT_METHODS = [
  { value: 'mpesa', label: 'M-Pesa' },
  { value: 'ecocash', label: 'EcoCash' },
  { value: 'bank', label: 'Bank Transfer' },
];

// ==================== UTILITIES ====================
const exportCSV = (filename, rows) => {
  if (!rows?.length) return;

  const excludedColumns = ['qualifications', 'year_obtained'];
  const headers = Object.keys(rows[0]).filter(h => !excludedColumns.includes(h));
  
  const csvContent = [
    headers.join(','),
    ...rows.map(row => headers.map(h => JSON.stringify(row[h] || '')).join(','))
  ].join('\n');

  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);
  
  link.href = url;
  link.download = filename;
  link.style.visibility = 'hidden';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

const exportToPDF = (formData, type = 'application') => {
  if (!formData) return;

  try {
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    const margin = 20;
    let yPos = 20;

    // Header
    doc.setFillColor(255, 255, 255);
    doc.rect(0, 0, pageWidth, 60, 'F');
    doc.setDrawColor(200, 200, 200);
    doc.line(margin, 60, pageWidth - margin, 60);

    // Logo
    try {
      doc.addImage(logo, 'PNG', margin, 15, 30, 30);
    } catch {
      doc.setFillColor(240, 240, 240);
      doc.rect(margin, 15, 30, 30, 'F');
      doc.setFontSize(8);
      doc.setTextColor(150, 150, 150);
      doc.text('LOGO', margin + 15, 30, { align: 'center' });
    }

    // Organization details
    doc.setFontSize(16);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(0, 0, 0);
    doc.text('LESOTHO NURSING AND', margin + 40, 20);
    doc.text('MIDWIFERY ORGANISATION', margin + 40, 28);

    // Address
    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(100, 100, 100);
    const addressLines = [
      'No.388 Nightingale Road,',
      'P.O Box 473,',
      'Maseru 100,',
      'Lesotho.'
    ];
    addressLines.forEach((line, i) => {
      doc.text(line, pageWidth - margin, 20 + (i * 5), { align: 'right' });
    });

    yPos = 70;
    
    // Title
    doc.setFontSize(18);
    doc.setFont('helvetica', 'bold');
    doc.text(
      `${type === 'app' ? 'Membership Application' : 'Member Details'} - ${formData.name || 'Unknown'}`,
      pageWidth / 2, yPos, { align: 'center' }
    );
    yPos += 15;

    // Employment number
    doc.setFontSize(12);
    doc.setFont('helvetica', 'normal');
    doc.text(`Employment Number: ${formData.employment_number || 'N/A'}`, pageWidth / 2, yPos, { align: 'center' });
    yPos += 20;

    // Form sections
    const sections = {
      'Personal Information': [
        'title', 'name', 'email', 'gender', 'marital_status', 'id_number',
        'nationality', 'date_of_birth', 'phone_number'
      ],
      'Employment Details': [
        'employment_number', 'grade', 'employer_name', 'duty_station', 'district', 'type_of_employment'
      ],
      'Payment Information': ['payment_frequency', 'payment_method'],
      'Status': [{ label: 'Status', value: formData.status || (type === 'member' ? 'Approved' : 'Pending') }]
    };

    Object.entries(sections).forEach(([sectionName, fields]) => {
      if (yPos > 250) {
        doc.addPage();
        yPos = 30;
        doc.setFontSize(10);
        doc.setTextColor(100, 100, 100);
        doc.text(`LENMO - ${formData.name}`, margin, 15);
        doc.line(margin, 18, pageWidth - margin, 18);
      }

      doc.setFont('helvetica', 'bold');
      doc.setFontSize(11);
      doc.setTextColor(0, 0, 0);
      doc.text(sectionName, margin, yPos);
      yPos += 10;

      doc.setFont('helvetica', 'normal');
      doc.setFontSize(10);
      
      fields.forEach(field => {
        if (typeof field === 'string') {
          const label = field.replace(/_/g, ' ');
          const value = formData[field] || 'N/A';
          doc.text(`${label}:`, margin, yPos);
          doc.text(value, margin + 60, yPos);
        } else {
          doc.text(`${field.label}:`, margin, yPos);
          doc.text(field.value, margin + 60, yPos);
        }
        yPos += 8;
      });
      yPos += 5;
    });

    // Footer
    const pageCount = doc.internal.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      doc.setFontSize(8);
      doc.setTextColor(128, 128, 128);
      doc.text(`Page ${i} of ${pageCount}`, pageWidth / 2, 290, { align: 'center' });
      
      if (i === pageCount) {
        doc.text(`Generated: ${new Date().toLocaleString()}`, pageWidth / 2, 285, { align: 'center' });
      }
    }

    const fileName = `LENMO_${type === 'app' ? 'Application' : 'Member'}_${formData.employment_number || 'unknown'}.pdf`;
    doc.save(fileName);
  } catch (error) {
    console.error('PDF Error:', error);
    alert('Failed to generate PDF');
  }
};

// ==================== COMPONENTS ====================
const Modal = ({ children, onClose, title }) => (
  <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
    <div className="modal-content" role="dialog" aria-modal="true">
      <div className="modal-header">
        <h2 className="modal-title">{title}</h2>
        <button className="modal-close" onClick={onClose} aria-label="Close">×</button>
      </div>
      {children}
    </div>
  </div>
);

const LoadingSpinner = () => (
  <div className="loading-state">
    <div className="loading-spinner" />
    <p>Loading...</p>
  </div>
);

const EmptyState = ({ message }) => (
  <div className="empty-state">{message}</div>
);

const ManualPayerEntryForm = ({ form, onChange, onSubmit, members, importEmp, onImportEmpChange, onImport }) => (
  <div className="transaction-form-section">
    <h4 className="transaction-form-title">Add Manual Payer (M-Pesa / EcoCash / Bank)</h4>
    <p style={{ margin: '0 0 1rem', fontSize: '0.9rem', color: 'var(--text-muted, #64748b)' }}>
      These members are not on auto bank deduction. Portal login details are emailed automatically on submit.
    </p>
    <form className="transaction-form" onSubmit={onSubmit}>
      <div className="transaction-form-grid">
        <div className="form-group">
          <label className="form-label">Title</label>
          <select className="form-input" value={form.title} onChange={(e) => onChange({ title: e.target.value })}>
            <option value="">—</option>
            <option value="Mr">Mr</option>
            <option value="Mrs">Mrs</option>
            <option value="Ms">Ms</option>
            <option value="Dr">Dr</option>
          </select>
        </div>
        <div className="form-group">
          <label className="form-label">Full Name</label>
          <input className="form-input" value={form.name} onChange={(e) => onChange({ name: e.target.value })} required />
        </div>
        <div className="form-group">
          <label className="form-label">Employment Number</label>
          <input className="form-input" value={form.employment_number} onChange={(e) => onChange({ employment_number: e.target.value })} required />
        </div>
        <div className="form-group">
          <label className="form-label">Email</label>
          <input type="email" className="form-input" value={form.email} onChange={(e) => onChange({ email: e.target.value })} required />
        </div>
        <div className="form-group">
          <label className="form-label">Phone</label>
          <input className="form-input" value={form.phone_number} onChange={(e) => onChange({ phone_number: e.target.value })} />
        </div>
        <div className="form-group">
          <label className="form-label">Preferred Payment</label>
          <select className="form-input" value={form.preferred_payment_method} onChange={(e) => onChange({ preferred_payment_method: e.target.value })} required>
            {PAYMENT_METHODS.map(pm => (
              <option key={pm.value} value={pm.value}>{pm.label}</option>
            ))}
          </select>
        </div>
        <div className="form-group">
          <label className="form-label">Notes</label>
          <input className="form-input" value={form.notes} onChange={(e) => onChange({ notes: e.target.value })} placeholder="Optional" />
        </div>
      </div>
      <div className="transaction-form-actions">
        <button type="submit" className="btn btn-primary">Add Manual Payer &amp; Email Portal Login</button>
      </div>
    </form>

    <h4 className="transaction-form-title" style={{ marginTop: '1.5rem' }}>Import from Existing Members</h4>
    <div className="transaction-form-grid">
      <div className="form-group">
        <label className="form-label">Existing Member</label>
        <select className="form-input" value={importEmp} onChange={(e) => onImportEmpChange(e.target.value)}>
          <option value="">Select member to import...</option>
          {members.map(m => (
            <option key={m.employment_number} value={m.employment_number}>
              {m.name} ({m.employment_number})
            </option>
          ))}
        </select>
      </div>
      <div className="form-group" style={{ display: 'flex', alignItems: 'flex-end' }}>
        <button type="button" className="btn btn-secondary" onClick={onImport} disabled={!importEmp}>
          Import as Manual Payer
        </button>
      </div>
    </div>
  </div>
);

const TransactionEntryForm = ({ members, form, onChange, onSubmit, onSendReminders }) => (
  <div className="transaction-form-section">
    <h4 className="transaction-form-title">Record New Payment</h4>
    <p style={{ margin: '0 0 1rem', fontSize: '0.9rem', color: 'var(--text-muted, #64748b)' }}>
      Only people in Manual Payers can be selected here.
    </p>
    <form className="transaction-form" onSubmit={onSubmit}>
      <div className="transaction-form-grid">
        <div className="form-group">
          <label className="form-label">Manual Payer</label>
          <select
            className="form-input"
            value={form.employment_number}
            onChange={(e) => onChange({ employment_number: e.target.value })}
            required
          >
            <option value="">Select manual payer...</option>
            {members.map(m => (
              <option key={m.employment_number} value={m.employment_number}>
                {m.name} ({m.employment_number})
              </option>
            ))}
          </select>
        </div>
        <div className="form-group">
          <label className="form-label">Amount (M)</label>
          <input
            type="number" step="0.01" className="form-input"
            value={form.amount}
            onChange={(e) => onChange({ amount: e.target.value })}
            required
          />
        </div>
        <div className="form-group">
          <label className="form-label">Payment Method</label>
          <select
            className="form-input"
            value={form.payment_method}
            onChange={(e) => onChange({ payment_method: e.target.value })}
            required
          >
            {PAYMENT_METHODS.map(pm => (
              <option key={pm.value} value={pm.value}>{pm.label}</option>
            ))}
          </select>
        </div>
        <div className="form-group">
          <label className="form-label">Reference Number</label>
          <input
            type="text" className="form-input"
            value={form.reference_number}
            onChange={(e) => onChange({ reference_number: e.target.value })}
            placeholder="M-Pesa / EcoCash / Bank ref"
          />
        </div>
        <div className="form-group">
          <label className="form-label">Transaction Date</label>
          <input
            type="date" className="form-input"
            value={form.transaction_date}
            onChange={(e) => onChange({ transaction_date: e.target.value })}
            required
          />
        </div>
        <div className="form-group">
          <label className="form-label">Notes</label>
          <input
            type="text" className="form-input"
            value={form.notes}
            onChange={(e) => onChange({ notes: e.target.value })}
            placeholder="Optional notes"
          />
        </div>
      </div>
      <div className="transaction-form-actions">
        <button type="submit" className="btn btn-primary">Save Transaction</button>
        <button type="button" className="btn btn-secondary" onClick={onSendReminders}>
          Send Payment Reminders
        </button>
      </div>
    </form>
  </div>
);

const MobileMenuToggle = ({ isOpen, onClick }) => (
  <button 
    className="mobile-menu-toggle"
    onClick={onClick}
    aria-label={isOpen ? 'Close menu' : 'Open menu'}
    aria-expanded={isOpen}
  >
    <span className="hamburger-icon">
      <span className="hamburger-line"></span>
      <span className="hamburger-line"></span>
      <span className="hamburger-line"></span>
    </span>
  </button>
);

// ==================== MAIN COMPONENT ====================
const AdminDashboard = () => {
  const { isAdmin, user, logout } = useAuth();
  const [state, setState] = useState({
    applications: [],
    members: [],
    manualPayers: [],
    subscribers: [],
    transactions: [],
    transactionStats: null,
    unpaidMembers: [],
    loading: { apps: false, members: false, subscribers: false, transactions: false, manualPayers: false },
    viewing: null,
    editing: null,
    globalSearch: '',
    notification: { message: '', type: '' },
    activeMenu: 'dashboard',
    sidebarCollapsed: false,
    mobileMenuOpen: false,
    isMobile: window.innerWidth <= 768,
    windowWidth: window.innerWidth,
    importPayerEmp: '',
    manualPayerForm: {
      title: '',
      name: '',
      employment_number: '',
      email: '',
      phone_number: '',
      preferred_payment_method: 'mpesa',
      notes: '',
    },
    transactionForm: {
      employment_number: '',
      amount: '50.00',
      payment_method: 'mpesa',
      reference_number: '',
      transaction_date: new Date().toISOString().split('T')[0],
      notes: '',
    },
  });

  const navigate = useNavigate();
  const tableContainerRef = useRef(null);
  const mainContentRef = useRef(null);

  // ==================== STATE UPDATERS ====================
  const updateState = useCallback((updates) => {
    setState(prev => ({ ...prev, ...updates }));
  }, []);

  const setLoading = useCallback((key, value) => {
    setState(prev => ({
      ...prev,
      loading: { ...prev.loading, [key]: value }
    }));
  }, []);

  // ==================== NOTIFICATION ====================
  const showNotification = useCallback((message, type = 'info') => {
    updateState({ notification: { message, type } });
    setTimeout(() => updateState({ notification: { message: '', type: '' } }), 5000);
  }, [updateState]);

  // ==================== WINDOW RESIZE HANDLER ====================
  useEffect(() => {
    const handleResize = () => {
      const width = window.innerWidth;
      const isMobile = width <= 768;
      
      updateState({ 
        windowWidth: width,
        isMobile,
        mobileMenuOpen: isMobile ? state.mobileMenuOpen : false,
        sidebarCollapsed: width < 1024 ? true : state.sidebarCollapsed
      });
    };

    window.addEventListener('resize', handleResize);
    handleResize();

    return () => window.removeEventListener('resize', handleResize);
  }, [updateState, state.mobileMenuOpen, state.sidebarCollapsed]);

  // ==================== API CALLS ====================
  const fetchData = useCallback(async (endpoint, stateKey, loadingKey) => {
    setLoading(loadingKey, true);
    try {
      const { data } = await api.get(endpoint);
      updateState({ [stateKey]: data });
    } catch (error) {
      console.error(`Failed to load ${stateKey}:`, error);
      showNotification(`Failed to load ${stateKey}`, 'error');
    } finally {
      setLoading(loadingKey, false);
    }
  }, [setLoading, updateState, showNotification]);

  const fetchApplications = useCallback(
    () => fetchData(ENDPOINTS.APPLICATIONS, 'applications', 'apps'),
    [fetchData]
  );

  const fetchMembers = useCallback(
    () => fetchData(ENDPOINTS.MEMBERS, 'members', 'members'),
    [fetchData]
  );

  const fetchManualPayers = useCallback(async () => {
    setLoading('manualPayers', true);
    try {
      const res = await api.get('/manual-payers');
      updateState({ manualPayers: Array.isArray(res.data) ? res.data : [] });
    } catch (error) {
      console.error('Failed to load manual payers:', error);
      showNotification(
        error.response?.data?.message || 'Failed to load manual payers',
        'error'
      );
    } finally {
      setLoading('manualPayers', false);
    }
  }, [setLoading, updateState, showNotification]);

  const fetchSubscribers = useCallback(
    () => fetchData(ENDPOINTS.SUBSCRIBERS, 'subscribers', 'subscribers'),
    [fetchData]
  );

  const fetchTransactions = useCallback(async () => {
    setLoading('transactions', true);
    try {
      // Load separately so one failing endpoint does not wipe the whole section
      const [txResult, statsResult, unpaidResult] = await Promise.allSettled([
        api.get('/transactions'),
        api.get('/transactions/stats'),
        api.get('/transactions/unpaid'),
      ]);

      const transactions =
        txResult.status === 'fulfilled' && Array.isArray(txResult.value.data)
          ? txResult.value.data
          : [];
      const transactionStats =
        statsResult.status === 'fulfilled' ? statsResult.value.data : null;
      const unpaidMembers =
        unpaidResult.status === 'fulfilled'
          ? unpaidResult.value.data?.members || []
          : [];

      updateState({ transactions, transactionStats, unpaidMembers });

      const failures = [txResult, statsResult, unpaidResult]
        .filter((r) => r.status === 'rejected')
        .map((r) => r.reason?.response?.data?.error || r.reason?.response?.data?.message || r.reason?.message);

      if (failures.length) {
        console.error('Transaction endpoint errors:', failures);
        showNotification(`Transactions partially loaded: ${failures[0]}`, 'warning');
      }
    } catch (error) {
      console.error('Failed to load transactions:', error);
      showNotification(
        error.response?.data?.message || 'Failed to load transactions',
        'error'
      );
    } finally {
      setLoading('transactions', false);
    }
  }, [setLoading, updateState, showNotification]);

  const recordTransaction = async (e) => {
    e.preventDefault();
    try {
      await api.post('/transactions', state.transactionForm);
      showNotification('Transaction recorded successfully', 'success');
      updateState({
        transactionForm: {
          employment_number: '', amount: '50.00', payment_method: 'mpesa',
          reference_number: '', transaction_date: new Date().toISOString().split('T')[0], notes: '',
        },
      });
      await fetchTransactions();
    } catch (error) {
      showNotification(error.response?.data?.message || 'Failed to record transaction', 'error');
    }
  };

  const addManualPayer = async (e) => {
    e.preventDefault();
    try {
      const res = await api.post('/manual-payers', state.manualPayerForm);
      const emailNote = res.data?.emailSent
        ? 'Portal login emailed.'
        : `Portal created; email not sent: ${res.data?.emailError || 'check email config'}`;
      showNotification(`Manual payer added. ${emailNote}`, res.data?.emailSent ? 'success' : 'warning');
      updateState({
        manualPayerForm: {
          title: '', name: '', employment_number: '', email: '', phone_number: '',
          preferred_payment_method: 'mpesa', notes: '',
        },
      });
      await fetchManualPayers();
    } catch (error) {
      showNotification(error.response?.data?.message || 'Failed to add manual payer', 'error');
    }
  };

  const importManualPayer = async () => {
    if (!state.importPayerEmp) return;
    if (!window.confirm(`Add this member to Manual Payers and email portal login?`)) return;
    try {
      const res = await api.post(`/manual-payers/from-member/${state.importPayerEmp}`, {
        preferred_payment_method: 'mpesa',
      });
      const emailNote = res.data?.emailSent
        ? 'Portal login emailed.'
        : `Email not sent: ${res.data?.emailError || 'check email config'}`;
      showNotification(`Imported. ${emailNote}`, res.data?.emailSent ? 'success' : 'warning');
      updateState({ importPayerEmp: '' });
      await fetchManualPayers();
    } catch (error) {
      showNotification(error.response?.data?.message || 'Failed to import member', 'error');
    }
  };

  const deleteManualPayer = async (payer) => {
    if (!window.confirm(`Remove ${payer.name} from Manual Payers?`)) return;
    try {
      await api.delete(`/manual-payers/${payer.id}`);
      showNotification('Manual payer removed', 'success');
      await fetchManualPayers();
    } catch (error) {
      showNotification(error.response?.data?.message || 'Failed to remove', 'error');
    }
  };

  const deleteTransaction = async (tx) => {
    if (!window.confirm(`Delete transaction for ${tx.member_name} (${tx.payment_month})?`)) return;
    try {
      await api.delete(`/transactions/${tx.id}`);
      showNotification('Transaction deleted', 'success');
      await fetchTransactions();
    } catch (error) {
      showNotification('Failed to delete transaction', 'error');
    }
  };

  const sendReminders = async () => {
    if (!window.confirm('Send payment reminders to unpaid manual payers (after the 5th)?')) return;
    try {
      const res = await api.post('/members/reminders/send');
      const result = res.data.results || {};
      if (result.skipped) {
        showNotification(`Reminders skipped: ${result.reason || 'Not due yet'}`, 'info');
        return;
      }
      if (result.error) {
        showNotification(`Reminder error: ${result.error}`, 'error');
        return;
      }
      const sent = result.sent || 0;
      const failed = result.failed || 0;
      const eligible = result.eligible || 0;
      showNotification(
        `Manual payer reminders (${result.month || 'current month'}) — eligible: ${eligible}, sent: ${sent}, failed: ${failed}`,
        sent > 0 ? 'success' : 'info'
      );
    } catch (error) {
      showNotification('Failed to send reminders', 'error');
    }
  };

  const setupTransactionTables = async () => {
    try {
      const res = await api.post('/transactions/setup');
      showNotification(res.data.message || 'Transaction tables created', 'success');
      await fetchTransactions();
    } catch (error) {
      showNotification(
        error.response?.data?.message || 'Failed to setup transaction tables',
        'error'
      );
    }
  };

  const provisionExistingMemberPortals = async () => {
    if (!window.confirm('Create portal logins for all existing members without accounts?')) return;
    try {
      const res = await api.post('/members/portal/provision-existing');
      const created = res.data?.created || 0;
      const credentials = res.data?.credentials || [];
      showNotification(res.data?.message || `Created ${created} portal account(s)`, 'success');

      if (credentials.length > 0) {
        exportCSV('member_portal_credentials.csv', credentials);
      }
    } catch (error) {
      showNotification(
        error.response?.data?.message || 'Failed to provision member portals',
        'error'
      );
    }
  };

  useEffect(() => {
    const publicNavbar = document.querySelector('.navbar') || document.querySelector('nav:not(.sidebar-nav)');
    if (publicNavbar) {
      publicNavbar.style.display = 'none';
    }

    document.body.classList.add('admin-mode');

    fetchApplications();
    fetchMembers();
    fetchManualPayers();
    fetchSubscribers();
    fetchTransactions();

    return () => {
      const publicNavbar = document.querySelector('.navbar') || document.querySelector('nav:not(.sidebar-nav)');
      if (publicNavbar) {
        publicNavbar.style.display = '';
      }
      document.body.classList.remove('admin-mode');
    };
  }, [fetchApplications, fetchMembers, fetchManualPayers, fetchSubscribers, fetchTransactions]);

  // ==================== DELETE FUNCTIONS ====================
  const deleteApplication = async (application) => {
    if (!window.confirm(`Are you sure you want to delete the application for ${application.name}? This action cannot be undone.`)) return;

    setLoading('apps', true);
    try {
      await api.delete(ENDPOINTS.DELETE_APPLICATION(application.employment_number));
      showNotification(`Application for ${application.name} deleted successfully`, 'success');
      await fetchApplications();
      
      // Close modal if viewing deleted item
      if (state.viewing?.data?.employment_number === application.employment_number) {
        closeModal();
      }
    } catch (error) {
      console.error('Delete application error:', error);
      showNotification(`Failed to delete application: ${error.response?.data?.message || error.message}`, 'error');
    } finally {
      setLoading('apps', false);
    }
  };

  const deleteMember = async (member) => {
    if (!window.confirm(`Are you sure you want to delete member ${member.name}? This action cannot be undone.`)) return;

    setLoading('members', true);
    try {
      await api.delete(ENDPOINTS.DELETE_MEMBER(member.employment_number));
      showNotification(`Member ${member.name} deleted successfully`, 'success');
      await fetchMembers();
      
      // Close modal if viewing deleted item
      if (state.viewing?.data?.employment_number === member.employment_number) {
        closeModal();
      }
    } catch (error) {
      console.error('Delete member error:', error);
      showNotification(`Failed to delete member: ${error.response?.data?.message || error.message}`, 'error');
    } finally {
      setLoading('members', false);
    }
  };

  // ==================== FIXED APPROVE APPLICATION FUNCTION ====================
  const approveApplication = async (application) => {
    if (!window.confirm(`Approve membership for ${application.name}?`)) return;

    setLoading('apps', true);
    try {
      console.log(`📝 Approving application for: ${application.employment_number}`);
      
      // Make the approval request
      const response = await api.post(
        ENDPOINTS.APPROVE_APPLICATION(application.employment_number)
      );
      
      console.log('✅ Approval response:', response.data);
      
      // Check if approval was successful (your backend returns success: true)
      if (response.data.success === true) {
        // Build success message
        let successMessage = `${application.name} approved successfully`;
        
        // Check email status from the response data
        if (response.data.data?.emailSent === true) {
          successMessage += ` and email sent to ${application.email}`;
          showNotification(successMessage, 'success');
        } else if (response.data.data?.emailSent === false) {
          const emailError = response.data.data?.emailDetails?.error || 'Unknown email error';
          successMessage += ` but email failed: ${emailError}`;
          showNotification(successMessage, 'warning');
        } else {
          // Email not attempted or no email info
          if (response.data.data?.emailDetails?.error) {
            successMessage += ` but email failed: ${response.data.data.emailDetails.error}`;
            showNotification(successMessage, 'warning');
          } else {
            showNotification(successMessage, 'success');
          }
        }
        
        // Show newsletter subscription status
        if (response.data.data?.newsletterSubscribed === true) {
          console.log('📰 Member subscribed to newsletter');
        }
        
        // Refresh the data after successful approval
        await Promise.all([
          fetchApplications(),
          fetchMembers()
        ]);
        
        // Close any open modals
        if (state.viewing) {
          closeModal();
        }
        
      } else {
        // Backend returned success: false
        throw new Error(response.data.message || 'Approval failed');
      }
      
    } catch (error) {
      console.error('❌ Approval error details:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
        statusText: error.response?.statusText
      });
      
      // Display a user-friendly error message based on backend response
      let errorMessage = 'Unknown error occurred';
      
      if (error.response?.data) {
        // Your backend returns these fields
        errorMessage = error.response.data.message || 
                       error.response.data.error || 
                       'Approval failed';
        
        // Handle specific status codes
        if (error.response.status === 404) {
          errorMessage = 'Application not found. It may have been already approved or does not exist.';
        } else if (error.response.status === 409) {
          errorMessage = error.response.data.message || 'Application has already been processed.';
        } else if (error.response.status === 500) {
          errorMessage = 'Server error. Please check the server logs for details.';
        }
      } else if (error.request) {
        errorMessage = 'No response from server. Please check your connection.';
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      showNotification(`Failed to approve ${application.name}: ${errorMessage}`, 'error');
    } finally {
      setLoading('apps', false);
    }
  };
  
  // ==================== OTHER ACTIONS ====================
  const startEditing = useCallback((type, data) => 
    updateState({ editing: { type, data: { ...data } } }), [updateState]);

  const saveEdit = async () => {
    const { type, data } = state.editing;
    try {
      const endpoint = type === 'app' 
        ? ENDPOINTS.UPDATE_APPLICATION(data.employment_number)
        : ENDPOINTS.UPDATE_MEMBER(data.employment_number);
      
      await api.put(endpoint, data);
      showNotification(`${type === 'app' ? 'Application' : 'Member'} updated`, 'success');
      
      type === 'app' ? await fetchApplications() : await fetchMembers();
      updateState({ editing: null });
    } catch (error) {
      console.error('Update error:', error);
      showNotification('Update failed', 'error');
    }
  };

  const handleEditChange = useCallback((e) => {
    const { name, value } = e.target;
    setState(prev => ({
      ...prev,
      editing: {
        ...prev.editing,
        data: { ...prev.editing.data, [name]: value }
      }
    }));
  }, []);

  const viewDetails = useCallback((type, data) => 
    updateState({ viewing: { type, data } }), [updateState]);

  const closeModal = useCallback(() => 
    updateState({ viewing: null, editing: null }), [updateState]);

  const toggleMobileMenu = useCallback(() => {
    updateState({ mobileMenuOpen: !state.mobileMenuOpen });
    document.body.style.overflow = !state.mobileMenuOpen ? 'hidden' : '';
  }, [state.mobileMenuOpen, updateState]);

  const closeMobileMenu = useCallback(() => {
    updateState({ mobileMenuOpen: false });
    document.body.style.overflow = '';
  }, [updateState]);

  const toggleSidebar = useCallback(() => {
    updateState({ sidebarCollapsed: !state.sidebarCollapsed });
  }, [state.sidebarCollapsed, updateState]);

  // ==================== SEARCH ====================
  const globalSearchResults = useMemo(() => {
    const { globalSearch, applications, members } = state;
    if (!globalSearch.trim()) return { applications: [], members: [] };
    
    const query = globalSearch.toLowerCase().trim();
    const searchInItem = (item) => 
      Object.values(item).some(val => val?.toString().toLowerCase().includes(query));
    
    return {
      applications: applications.filter(searchInItem),
      members: members.filter(searchInItem)
    };
  }, [state.globalSearch, state.applications, state.members]);

  // ==================== HELPERS ====================
  const getStatus = useCallback((item, type) => {
    if (item.status) return { text: item.status, class: item.status.toLowerCase() };
    if (type === 'member') return { text: 'Approved', class: 'approved' };
    return { text: 'Pending', class: 'pending' };
  }, []);

  const getPageTitle = useCallback(() => {
    const { globalSearch, activeMenu } = state;
    if (globalSearch) return 'Search Results';
    
    const titles = {
      dashboard: 'Dashboard',
      applications: 'Applications',
      members: 'Members',
      manualPayers: 'Manual Payers',
      transactions: 'Transactions',
      subscribers: 'Subscribers'
    };
    return titles[activeMenu] || 'Dashboard';
  }, [state.globalSearch, state.activeMenu]);

  // ==================== RENDERERS ====================
  const renderTableRows = useCallback((data, type) => 
    data.map(item => {
      const status = getStatus(item, type);
      return (
        <tr key={item.employment_number} className="table-row">
          <td className="table-cell" data-label="Title">{item.title}</td>
          <td className="table-cell" data-label="Name">{item.name}</td>
          <td className="table-cell" data-label="Email">{item.email}</td>
          <td className="table-cell" data-label="Gender">{item.gender}</td>
          <td className="table-cell" data-label="Marital Status">{item.marital_status}</td>
          <td className="table-cell" data-label="ID Number">{item.id_number}</td>
          <td className="table-cell" data-label="Emp #">{item.employment_number}</td>
          <td className="table-cell" data-label="Grade">{item.grade}</td>
          <td className="table-cell" data-label="Phone">{item.phone_number}</td>
          <td className="table-cell" data-label="Nationality">{item.nationality}</td>
          <td className="table-cell" data-label="DOB">{item.date_of_birth}</td>
          <td className="table-cell" data-label="Employer">{item.employer_name}</td>
          <td className="table-cell" data-label="Duty Station">{item.duty_station}</td>
          <td className="table-cell" data-label="District">{item.district}</td>
          <td className="table-cell" data-label="Employment Type">{item.type_of_employment}</td>
          <td className="table-cell" data-label="Payment Freq">{item.payment_frequency}</td>
          <td className="table-cell" data-label="Payment Method">{item.payment_method}</td>
          <td className="table-cell" data-label="Status">
            <span className={`status-badge status-${status.class}`}>{status.text}</span>
          </td>
          <td className="table-cell" data-label="Actions">
            <div className="action-buttons">
              {type === 'app' && isAdmin && (
                <button 
                  className="btn btn-approve" 
                  onClick={() => approveApplication(item)} 
                  title="Approve"
                  aria-label="Approve application"
                >
                  ✓
                </button>
              )}
              <button 
                className="btn btn-view" 
                onClick={() => viewDetails(type, item)} 
                title="View"
                aria-label="View details"
              >
                👁
              </button>
              <button 
                className="btn btn-download" 
                onClick={() => exportToPDF(item, type)} 
                title="PDF"
                aria-label="Download PDF"
              >
                📄
              </button>
              {isAdmin && (
                <>
                  <button 
                    className="btn btn-edit" 
                    onClick={() => startEditing(type, item)} 
                    title="Edit"
                    aria-label="Edit record"
                  >
                    ✎
                  </button>
                  <button 
                    className="btn btn-delete" 
                    onClick={() => type === 'app' ? deleteApplication(item) : deleteMember(item)} 
                    title="Delete"
                    aria-label="Delete record"
                  >
                    🗑️
                  </button>
                </>
              )}
            </div>
          </td>
        </tr>
      );
    }), [approveApplication, startEditing, viewDetails, getStatus, deleteApplication, deleteMember, isAdmin]);

  const renderMobileCardView = useCallback((items, type) => (
    <div className="mobile-card-view">
      {items.map(item => {
        const status = getStatus(item, type);
        return (
          <div key={item.employment_number} className="mobile-card">
            <div className="mobile-card-header">
              <h3 className="mobile-card-title">{item.name}</h3>
              <span className={`status-badge status-${status.class}`}>{status.text}</span>
            </div>
            
            <div className="mobile-card-body">
              <div className="mobile-card-row">
                <span className="mobile-card-label">Email:</span>
                <span className="mobile-card-value">{item.email}</span>
              </div>
              <div className="mobile-card-row">
                <span className="mobile-card-label">Emp #:</span>
                <span className="mobile-card-value">{item.employment_number}</span>
              </div>
              <div className="mobile-card-row">
                <span className="mobile-card-label">Employer:</span>
                <span className="mobile-card-value">{item.employer_name}</span>
              </div>
              <div className="mobile-card-row">
                <span className="mobile-card-label">Phone:</span>
                <span className="mobile-card-value">{item.phone_number}</span>
              </div>
            </div>

            <div className="mobile-card-actions">
              {type === 'app' && isAdmin && (
                <button 
                  className="btn btn-approve" 
                  onClick={() => approveApplication(item)}
                >
                  ✓ Approve
                </button>
              )}
              <button 
                className="btn btn-view" 
                onClick={() => viewDetails(type, item)}
              >
                👁 View
              </button>
              <button 
                className="btn btn-download" 
                onClick={() => exportToPDF(item, type)}
              >
                📄 PDF
              </button>
              {isAdmin && (
                <>
                  <button 
                    className="btn btn-edit" 
                    onClick={() => startEditing(type, item)}
                  >
                    ✎ Edit
                  </button>
                  <button 
                    className="btn btn-delete" 
                    onClick={() => type === 'app' ? deleteApplication(item) : deleteMember(item)}
                  >
                    🗑️ Delete
                  </button>
                </>
              )}
            </div>
          </div>
        );
      })}
    </div>
  ), [approveApplication, startEditing, viewDetails, getStatus, deleteApplication, deleteMember, isAdmin]);

  const renderCompactTable = useCallback((items, type) => (
    <div className="table-responsive">
      <div className="table-container" ref={tableContainerRef}>
        <table className="dashboard-table compact">
          <thead>
            <tr>
              <th className="table-header">Name</th>
              <th className="table-header">Email</th>
              <th className="table-header">Emp #</th>
              <th className="table-header">Employer</th>
              <th className="table-header">Status</th>
              <th className="table-header">Actions</th>
            </tr>
          </thead>
          <tbody>
            {items.map(item => {
              const status = getStatus(item, type);
              return (
                <tr key={item.employment_number} className="table-row">
                  <td className="table-cell" data-label="Name">{item.name}</td>
                  <td className="table-cell" data-label="Email">{item.email}</td>
                  <td className="table-cell" data-label="Emp #">{item.employment_number}</td>
                  <td className="table-cell" data-label="Employer">{item.employer_name}</td>
                  <td className="table-cell" data-label="Status">
                    <span className={`status-badge status-${status.class}`}>{status.text}</span>
                  </td>
                  <td className="table-cell" data-label="Actions">
                    <div className="action-buttons">
                      <button 
                        className="btn btn-view" 
                        onClick={() => viewDetails(type, item)}
                        aria-label="View details"
                      >
                        👁
                      </button>
                      <button 
                        className="btn btn-download" 
                        onClick={() => exportToPDF(item, type)}
                        aria-label="Download PDF"
                      >
                        📄
                      </button>
                      {isAdmin && (
                        <>
                          <button 
                            className="btn btn-edit" 
                            onClick={() => startEditing(type, item)}
                            aria-label="Edit record"
                          >
                            ✎
                          </button>
                          <button 
                            className="btn btn-delete" 
                            onClick={() => type === 'app' ? deleteApplication(item) : deleteMember(item)}
                            aria-label="Delete record"
                          >
                            🗑️
                          </button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  ), [viewDetails, startEditing, getStatus, deleteApplication, deleteMember, isAdmin]);

  const renderSearchResults = useCallback(() => {
    const { applications: appResults, members: memResults } = globalSearchResults;
    const total = appResults.length + memResults.length;

    if (total === 0) {
      return <EmptyState message={`No results found for "${state.globalSearch}"`} />;
    }

    return (
      <div className="search-results">
        <h3 className="results-title">Found {total} results</h3>
        {appResults.length > 0 && (
          <div className="result-section">
            <h4 className="result-section-title">
              Applications ({appResults.length})
              <button 
                className="btn btn-sm" 
                onClick={() => exportCSV('search_applications.csv', appResults)}
                aria-label="Export applications"
              >
                📊 Export
              </button>
            </h4>
            {renderCompactTable(appResults, 'app')}
          </div>
        )}
        {memResults.length > 0 && (
          <div className="result-section">
            <h4 className="result-section-title">
              Members ({memResults.length})
              <button 
                className="btn btn-sm" 
                onClick={() => exportCSV('search_members.csv', memResults)}
                aria-label="Export members"
              >
                📊 Export
              </button>
            </h4>
            {renderCompactTable(memResults, 'member')}
          </div>
        )}
      </div>
    );
  }, [globalSearchResults, state.globalSearch, renderCompactTable]);

  const renderDashboard = useCallback(() => {
    const { applications, members, subscribers, transactionStats } = state;
    
    return (
      <section className="content-section dashboard-section">
        {!isAdmin && (
          <div className="view-only-banner">
            View-only mode — contact an admin to make changes
          </div>
        )}
        <div className="stats-grid">
          <div 
            className="stat-card stat-apps" 
            onClick={() => {
              updateState({ activeMenu: 'applications' });
              closeMobileMenu();
            }}
            role="button"
            tabIndex={0}
            onKeyPress={(e) => e.key === 'Enter' && updateState({ activeMenu: 'applications' })}
          >
            <div className="stat-icon-wrapper">📝</div>
            <div className="stat-content">
              <h3 className="stat-title">Pending Applications</h3>
              <p className="stat-value">{applications.length}</p>
            </div>
          </div>
          
          <div 
            className="stat-card stat-members" 
            onClick={() => {
              updateState({ activeMenu: 'members' });
              closeMobileMenu();
            }}
            role="button"
            tabIndex={0}
            onKeyPress={(e) => e.key === 'Enter' && updateState({ activeMenu: 'members' })}
          >
            <div className="stat-icon-wrapper">👥</div>
            <div className="stat-content">
              <h3 className="stat-title">Active Members</h3>
              <p className="stat-value">{members.length}</p>
            </div>
          </div>
          
          <div 
            className="stat-card stat-subscribers" 
            onClick={() => {
              updateState({ activeMenu: 'subscribers' });
              closeMobileMenu();
            }}
            role="button"
            tabIndex={0}
            onKeyPress={(e) => e.key === 'Enter' && updateState({ activeMenu: 'subscribers' })}
          >
            <div className="stat-icon-wrapper">📧</div>
            <div className="stat-content">
              <h3 className="stat-title">Newsletter Subscribers</h3>
              <p className="stat-value">{subscribers.length}</p>
            </div>
          </div>

          {transactionStats && (
            <>
              <div 
                className="stat-card stat-paid"
                onClick={() => { updateState({ activeMenu: 'transactions' }); closeMobileMenu(); }}
                role="button" tabIndex={0}
              >
                <div className="stat-icon-wrapper">💰</div>
                <div className="stat-content">
                  <h3 className="stat-title">Manual Paid This Month</h3>
                  <p className="stat-value">{transactionStats.paidThisMonth}/{transactionStats.totalMembers}</p>
                </div>
              </div>
              <div 
                className="stat-card stat-unpaid"
                onClick={() => { updateState({ activeMenu: 'transactions' }); closeMobileMenu(); }}
                role="button" tabIndex={0}
              >
                <div className="stat-icon-wrapper">⚠️</div>
                <div className="stat-content">
                  <h3 className="stat-title">Manual Unpaid This Month</h3>
                  <p className="stat-value">{transactionStats.unpaidThisMonth}</p>
                </div>
              </div>
            </>
          )}
        </div>

        <div className="recent-activity">
          <h3 className="recent-title">Recent Applications</h3>
          <div className="recent-list">
            {applications.slice(0, 5).map(app => (
              <div key={app.employment_number} className="recent-item">
                <div className="recent-info">
                  <div className="recent-name">{app.name}</div>
                  <div className="recent-email">{app.email}</div>
                </div>
                <span className="status-badge status-pending">Pending</span>
              </div>
            ))}
          </div>
        </div>
      </section>
    );
  }, [state, updateState, closeMobileMenu, isAdmin]);

  const renderContent = useCallback(() => {
    const { globalSearch, activeMenu, applications, members, subscribers, loading, isMobile } = state;

    if (globalSearch.trim()) {
      return (
        <section className="content-section">
          <div className="section-header">
            <h2 className="section-title">Search Results</h2>
            <button 
              className="btn btn-secondary" 
              onClick={() => updateState({ globalSearch: '' })}
              aria-label="Clear search"
            >
              Clear
            </button>
          </div>
          {renderSearchResults()}
        </section>
      );
    }

    const sections = {
      applications: {
        data: applications,
        loading: loading.apps,
        title: 'Pending Applications',
        emptyMessage: 'No applications found',
        type: 'app',
        exportFile: 'applications.csv'
      },
      members: {
        data: members,
        loading: loading.members,
        title: 'Members',
        emptyMessage: 'No members found',
        type: 'member',
        exportFile: 'members.csv'
      },
      manualPayers: {
        data: state.manualPayers,
        loading: loading.manualPayers,
        title: 'Manual Payers',
        emptyMessage: 'No manual payers yet. Add people who pay via M-Pesa, EcoCash, or bank.',
        exportFile: 'manual_payers.csv',
        renderCustom: () => (
          <div>
            {isAdmin && (
              <ManualPayerEntryForm
                form={state.manualPayerForm}
                onChange={(updates) => updateState({
                  manualPayerForm: { ...state.manualPayerForm, ...updates }
                })}
                onSubmit={addManualPayer}
                members={state.members}
                importEmp={state.importPayerEmp}
                onImportEmpChange={(v) => updateState({ importPayerEmp: v })}
                onImport={importManualPayer}
              />
            )}
            <div className="table-responsive">
              <div className="table-container">
                <table className="dashboard-table">
                  <thead>
                    <tr>
                      <th className="table-header">Name</th>
                      <th className="table-header">Emp #</th>
                      <th className="table-header">Email</th>
                      <th className="table-header">Phone</th>
                      <th className="table-header">Payment</th>
                      <th className="table-header">Status</th>
                      {isAdmin && <th className="table-header">Actions</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {state.manualPayers.length === 0 ? (
                      <tr>
                        <td className="table-cell" colSpan={isAdmin ? 7 : 6}>
                          No manual payers yet.
                        </td>
                      </tr>
                    ) : state.manualPayers.map(p => (
                      <tr key={p.id || p.employment_number} className="table-row">
                        <td className="table-cell">{p.title ? `${p.title}. ${p.name}` : p.name}</td>
                        <td className="table-cell">{p.employment_number}</td>
                        <td className="table-cell">{p.email}</td>
                        <td className="table-cell">{p.phone_number || '—'}</td>
                        <td className="table-cell">
                          <span className={`payment-badge payment-${p.preferred_payment_method}`}>
                            {p.preferred_payment_method === 'mpesa' ? 'M-Pesa' : p.preferred_payment_method === 'ecocash' ? 'EcoCash' : 'Bank'}
                          </span>
                        </td>
                        <td className="table-cell">{p.status}</td>
                        {isAdmin && (
                          <td className="table-cell">
                            <button className="btn btn-delete" onClick={() => deleteManualPayer(p)} title="Remove">🗑️</button>
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        ),
      },
      subscribers: {
        data: subscribers,
        loading: loading.subscribers,
        title: 'Subscribers',
        emptyMessage: 'No subscribers found',
        renderCustom: () => (
          <div className="table-responsive">
            <div className="table-container">
              <table className="dashboard-table">
                <thead>
                  <tr>
                    <th className="table-header">#</th>
                    <th className="table-header">Email</th>
                    <th className="table-header">Subscribed On</th>
                  </tr>
                </thead>
                <tbody>
                  {subscribers.map((sub, i) => (
                    <tr key={sub.id || i} className="table-row">
                      <td className="table-cell" data-label="#">{i + 1}</td>
                      <td className="table-cell" data-label="Email">{sub.email}</td>
                      <td className="table-cell" data-label="Subscribed On">
                        {new Date(sub.created_at).toLocaleString()}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ),
        exportFile: 'subscribers.csv'
      },
      transactions: {
        data: state.transactions,
        loading: loading.transactions,
        title: 'Transactions',
        emptyMessage: 'No transactions recorded',
        renderCustom: () => (
          <div>
            {isAdmin && (
              <TransactionEntryForm
                members={state.manualPayers}
                form={state.transactionForm}
                onChange={(updates) => updateState({
                  transactionForm: { ...state.transactionForm, ...updates }
                })}
                onSubmit={recordTransaction}
                onSendReminders={sendReminders}
              />
            )}

            {state.transactionStats ? (
              <div className="transaction-stats-bar">
                <div className="tx-stat">
                  <span className="tx-stat-label">This Month</span>
                  <span className="tx-stat-value">M{state.transactionStats.totalAmount?.toFixed(2) || '0.00'}</span>
                </div>
                <div className="tx-stat">
                  <span className="tx-stat-label">Paid</span>
                  <span className="tx-stat-value">{state.transactionStats.paidThisMonth}/{state.transactionStats.totalMembers}</span>
                </div>
                <div className="tx-stat">
                  <span className="tx-stat-label">Unpaid</span>
                  <span className="tx-stat-value tx-stat-danger">{state.transactionStats.unpaidThisMonth}</span>
                </div>
              </div>
            ) : (
              <div className="transaction-stats-bar transaction-stats-warning">
                Payment tables are missing or not ready.
                {isAdmin && (
                  <button
                    type="button"
                    className="btn btn-secondary"
                    style={{ marginLeft: '0.75rem' }}
                    onClick={setupTransactionTables}
                  >
                    Setup Transaction Tables
                  </button>
                )}
              </div>
            )}

            <div className="table-responsive">
              <div className="table-container">
                <table className="dashboard-table">
                  <thead>
                    <tr>
                      <th className="table-header">Date</th>
                      <th className="table-header">Member</th>
                      <th className="table-header">Emp #</th>
                      <th className="table-header">Amount</th>
                      <th className="table-header">Method</th>
                      <th className="table-header">Reference</th>
                      <th className="table-header">Month</th>
                      {isAdmin && <th className="table-header">Actions</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {state.transactions.length === 0 ? (
                      <tr>
                        <td className="table-cell" colSpan={isAdmin ? 8 : 7}>
                          No transactions yet. Use the form above to record a payment.
                        </td>
                      </tr>
                    ) : state.transactions.map(tx => (
                      <tr key={tx.id} className="table-row">
                        <td className="table-cell">{tx.transaction_date}</td>
                        <td className="table-cell">{tx.member_name}</td>
                        <td className="table-cell">{tx.employment_number}</td>
                        <td className="table-cell">M{parseFloat(tx.amount).toFixed(2)}</td>
                        <td className="table-cell">
                          <span className={`payment-badge payment-${tx.payment_method}`}>
                            {tx.payment_method === 'mpesa' ? 'M-Pesa' : tx.payment_method === 'ecocash' ? 'EcoCash' : 'Bank'}
                          </span>
                        </td>
                        <td className="table-cell">{tx.reference_number || '—'}</td>
                        <td className="table-cell">{tx.payment_month}</td>
                        {isAdmin && (
                          <td className="table-cell">
                            <button className="btn btn-delete" onClick={() => deleteTransaction(tx)} title="Delete">🗑️</button>
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
            {state.unpaidMembers.length > 0 && (
              <div className="unpaid-section">
                <h4>Unpaid Manual Payers ({state.unpaidMembers.length})</h4>
                <div className="unpaid-list">
                  {state.unpaidMembers.map(m => (
                    <div key={m.employment_number} className="unpaid-item">
                      <span>{m.name}</span>
                      <span className="unpaid-emp">{m.employment_number}</span>
                      <span className="unpaid-email">{m.email}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        ),
        exportFile: 'transactions.csv'
      }
    };

    const current = sections[activeMenu];
    if (!current || activeMenu === 'dashboard') return renderDashboard();

    return (
      <section className="content-section">
        <div className="section-header">
          <h2 className="section-title">
            {current.title} <span className="record-count">({current.data.length})</span>
          </h2>
          <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
            {activeMenu === 'members' && isAdmin && (
              <button
                className="btn btn-secondary"
                onClick={provisionExistingMemberPortals}
                aria-label="Provision member portal accounts"
              >
                🔑 Create Portal Logins
              </button>
            )}
            <button 
              className="btn btn-export" 
              onClick={() => exportCSV(current.exportFile, current.data)}
              aria-label="Export to CSV"
            >
              📊 Export CSV
            </button>
          </div>
        </div>
        
        {current.loading ? (
          <LoadingSpinner />
        ) : current.renderCustom ? (
          current.renderCustom()
        ) : current.data.length === 0 ? (
          <EmptyState message={current.emptyMessage} />
        ) : (
          <>
            <div className="table-responsive desktop-only">
              <div className="table-container">
                <table className="dashboard-table">
                  <thead>
                    <tr>
                      {TABLE_HEADERS.map(h => (
                        <th key={h} className="table-header">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>{renderTableRows(current.data, current.type)}</tbody>
                </table>
              </div>
            </div>
            {renderMobileCardView(current.data, current.type)}
          </>
        )}
      </section>
    );
  }, [state, renderSearchResults, renderDashboard, renderTableRows, renderMobileCardView]);

  // ==================== RENDER ====================
  const { sidebarCollapsed, notification, viewing, editing, mobileMenuOpen, isMobile } = state;

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (mobileMenuOpen && !event.target.closest('.sidebar') && !event.target.closest('.mobile-menu-toggle')) {
        closeMobileMenu();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [mobileMenuOpen, closeMobileMenu]);

  return (
    <div className={`dashboard-container ${mobileMenuOpen ? 'menu-open' : ''}`}>
      <MobileMenuToggle isOpen={mobileMenuOpen} onClick={toggleMobileMenu} />

      {mobileMenuOpen && <div className="mobile-menu-overlay" onClick={closeMobileMenu} />}

      <aside className={`sidebar ${sidebarCollapsed ? 'collapsed' : ''} ${mobileMenuOpen ? 'mobile-open' : ''}`}>
        <div className="sidebar-header">
          <div className="logo-container">
            <img src={logo} alt="LENMO" className="sidebar-logo" />
            {(!sidebarCollapsed || isMobile) && (
              <div>
                <span className="logo-text">LeNMO</span>
                <span className="logo-subtext">{isAdmin ? 'Administrator' : 'Viewer access'}</span>
              </div>
            )}
          </div>
          {!isMobile && (
            <button 
              className="sidebar-toggle" 
              onClick={toggleSidebar}
              aria-label={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            >
              {sidebarCollapsed ? '→' : '←'}
            </button>
          )}
        </div>

        <nav className="sidebar-nav" aria-label="Main navigation">
          {MENU_ITEMS.map(item => {
            const count = state[item.key === 'dashboard' ? 'applications' : item.key]?.length;
            return (
              <button
                key={item.key}
                className={`nav-item ${state.activeMenu === item.key ? 'active' : ''}`}
                onClick={() => {
                  updateState({ activeMenu: item.key, globalSearch: '' });
                  closeMobileMenu();
                }}
                aria-label={item.label}
                aria-current={state.activeMenu === item.key ? 'page' : undefined}
              >
                <span className="nav-icon" aria-hidden="true">{item.icon}</span>
                {(!sidebarCollapsed || isMobile) && (
                  <>
                    <span className="nav-label">{item.label}</span>
                    {count > 0 && item.key !== 'dashboard' && (
                      <span className="nav-badge" aria-label={`${count} items`}>{count}</span>
                    )}
                  </>
                )}
              </button>
            );
          })}
        </nav>

        <div className="sidebar-footer">
          <button 
            className="nav-item" 
            onClick={() => {
              navigate('/');
              closeMobileMenu();
            }}
            aria-label="Go to home"
          >
            <span className="nav-icon" aria-hidden="true">🏠</span>
            {(!sidebarCollapsed || isMobile) && <span className="nav-label">Home</span>}
          </button>
          <button 
            className="nav-item logout-item" 
            onClick={() => {
              logout();
              const publicNavbar = document.querySelector('.navbar');
              if (publicNavbar) publicNavbar.style.display = '';
              document.body.classList.remove('admin-mode');
              navigate('/adminLogin');
              closeMobileMenu();
            }}
            aria-label="Logout"
          >
            <span className="nav-icon" aria-hidden="true">🚪</span>
            {(!sidebarCollapsed || isMobile) && <span className="nav-label">Logout</span>}
          </button>
        </div>
      </aside>

      <main className="main-content" ref={mainContentRef}>
        {notification.message && (
          <div 
            className={`notification notification-${notification.type}`}
            role="alert"
            aria-live="polite"
          >
            {notification.message}
          </div>
        )}

        <div className="content-container">
          <header className="content-header">
            <div className="header-left">
              <div className="header-meta">
                <span className={`role-chip ${isAdmin ? 'admin' : 'viewer'}`}>
                  {isAdmin ? 'Admin' : 'Viewer'}
                </span>
                {user?.email && <span className="header-user">{user.email}</span>}
              </div>
              <h1 className="page-title">{getPageTitle()}</h1>
              <p className="page-subtitle">
                {state.activeMenu === 'dashboard'
                  ? 'Overview of membership, payments, and association activity'
                  : state.activeMenu === 'manualPayers'
                    ? 'Self-pay members (M-Pesa, EcoCash, bank) — not auto bank deduction'
                  : state.activeMenu === 'transactions'
                    ? 'Record payments for manual payers only'
                    : 'Manage and review association records'}
              </p>
            </div>
            
            <div className="global-search-wrapper">
              <div className="global-search-box">
                <span className="global-search-icon" aria-hidden="true">🔍</span>
                <input
                  type="text"
                  placeholder="Search by name, email, emp #..."
                  value={state.globalSearch}
                  onChange={(e) => updateState({ globalSearch: e.target.value })}
                  className="global-search-input"
                  aria-label="Global search"
                />
                {state.globalSearch && (
                  <button 
                    className="global-search-clear" 
                    onClick={() => updateState({ globalSearch: '' })}
                    aria-label="Clear search"
                  >
                    ×
                  </button>
                )}
              </div>
            </div>
          </header>

          {renderContent()}
        </div>
      </main>

      {viewing && (
        <Modal onClose={closeModal} title={`${viewing.type === 'app' ? 'Application' : 'Member'} Details`}>
          <div className="view-details">
            {Object.entries(viewing.data).map(([key, val]) => (
              <div key={key} className="detail-row">
                <span className="detail-label">{key.replace(/_/g, ' ').toUpperCase()}:</span>
                <span className="detail-value">{val || '—'}</span>
              </div>
            ))}
          </div>
          <div className="modal-footer">
            <button 
              className="btn btn-primary" 
              onClick={() => exportToPDF(viewing.data, viewing.type)}
              aria-label="Download PDF"
            >
              📄 Download PDF
            </button>
            <button 
              className="btn btn-danger" 
              onClick={() => viewing.type === 'app' ? deleteApplication(viewing.data) : deleteMember(viewing.data)}
              aria-label="Delete record"
              style={{ display: isAdmin ? '' : 'none' }}
            >
              🗑️ Delete
            </button>
            <button 
              className="btn btn-secondary" 
              onClick={closeModal}
              aria-label="Close modal"
            >
              Close
            </button>
          </div>
        </Modal>
      )}

      {editing && isAdmin && (
        <Modal onClose={closeModal} title={`Edit ${editing.type === 'app' ? 'Application' : 'Member'}`}>
          <form className="edit-form" onSubmit={e => { e.preventDefault(); saveEdit(); }}>
            <div className="form-grid">
              {Object.keys(editing.data).map(field => (
                <div key={field} className="form-group">
                  <label htmlFor={field} className="form-label">
                    {field.replace(/_/g, ' ')}
                  </label>
                  <input
                    id={field}
                    type="text"
                    name={field}
                    value={editing.data[field] || ''}
                    onChange={handleEditChange}
                    disabled={field === 'employment_number'}
                    className="form-input"
                    aria-label={field.replace(/_/g, ' ')}
                  />
                </div>
              ))}
            </div>
            <div className="modal-footer">
              <button type="submit" className="btn btn-primary">Save Changes</button>
              <button type="button" className="btn btn-secondary" onClick={closeModal}>Cancel</button>
            </div>
          </form>
        </Modal>
      )}
    </div>
  );
};

export default AdminDashboard;